import { Link } from "react-router-dom";
import { Shield, Award, Target, Users, Heart, Clock, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";

const CONSULTING_IMAGE = "https://images.unsplash.com/photo-1758519290111-bfbd61b32d49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxjb25maWRlbnQlMjBidXNpbmVzcyUyMG93bmVyJTIwZGl2ZXJzZSUyMHRlYW0lMjBtZWV0aW5nJTIwcHJvZmVzc2lvbmFsJTIwb2ZmaWNlfGVufDB8fHx8MTc3MDcxNDQ2OHww&ixlib=rb-4.1.0&q=85";

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center gap-4 mb-8">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white border border-white/20">
                <Shield className="h-4 w-4 mr-2 text-[#9D9045]" />
                Veteran Owned
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white border border-white/20">
                <Award className="h-4 w-4 mr-2 text-[#9D9045]" />
                NNA Certified
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              About OMW Business & Financial Resources
            </h1>
            <p className="text-xl text-gray-300">
              Best Business Consultants in The South
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src={CONSULTING_IMAGE} 
                alt="Business consulting meeting" 
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-8 -right-8 bg-[#9D9045] text-white p-8 rounded-xl shadow-lg max-w-xs">
                <div className="text-4xl font-bold mb-2">5+</div>
                <div className="text-lg">Years Helping Businesses Succeed</div>
              </div>
            </div>
            
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Our Story
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Helping small businesses find innovative solutions for startup, growth, and expansion. Our team of experienced business consultants here at OMW Business & Financial Resources have a background in a number of fields giving us a broad range of knowledge to draw from.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Whether you're looking for specific advice or need all-round assistance navigating the current business climate, we're on hand to guide and inform. As a Veteran-Owned Small Business, we understand the challenges entrepreneurs face and are committed to helping you overcome them.
              </p>
              
              <div className="grid grid-cols-2 gap-6 pt-6">
                {[
                  { icon: Target, label: "Mission Focused" },
                  { icon: Users, label: "Client Centered" },
                  { icon: Heart, label: "Passionate Service" },
                  { icon: Clock, label: "Always Available" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg bg-[#1E3A5F]/5 flex items-center justify-center">
                      <item.icon className="h-6 w-6 text-[#1E3A5F]" />
                    </div>
                    <span className="font-medium text-gray-700">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Services
            </h2>
            <p className="text-gray-600">
              Our experienced and professional team are able to help you find the right strategic solution for your business including defining a clear path to operational goals, marketing, financial structure, brand identity, and helping you identify ways of streamlining workflow processes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              "Business strategy and coaching",
              "Business startup/launch assistance",
              "Logo & product designing",
              "Branding consultation",
              "Mobile notary signing services",
              "Registered agent services",
              "Money management coaching",
              "Business credit building guidance"
            ].map((service, i) => (
              <div key={i} className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-sm">
                <CheckCircle className="h-6 w-6 text-[#9D9045] flex-shrink-0" />
                <span className="text-gray-700">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Credentials
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We possess the expertise, professionalism, and technical knowledge to conduct business with the public. Our team employs fully certified business consultants who possess the necessary credentials, experience, and commitment.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { title: "Veteran-Owned", date: "Since 2021" },
              { title: "Certified Background Screened", date: "Verified" },
              { title: "Certified Notary Signing Agent", date: "NNA Certified" },
              { title: "National Notary Association", date: "Member" }
            ].map((badge, i) => (
              <div key={i} className="text-center p-6 bg-gray-50 rounded-xl">
                <Award className="h-12 w-12 mx-auto text-[#9D9045] mb-4" />
                <h4 className="font-semibold text-[#1E3A5F] mb-2">{badge.title}</h4>
                <span className="text-sm text-gray-500">{badge.date}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Work Together?
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            Schedule a free discovery consultation and let us help you achieve your business goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-8 py-6 text-lg" data-testid="about-contact-btn">
                Contact Us Today
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1E3A5F] px-8 py-6 text-lg" data-testid="about-services-btn">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
